import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore, selectMembers, selectCollaborators } from '@/utils/store';
import { isSupabaseConfigured } from '@/lib/supabase';
import {
  fetchCollaborators,
  addInternalCollaborator,
  removeCollaborator as apiRemoveCollaborator,
  updateCollaboratorRole as apiUpdateCollaboratorRole,
  generateInviteCode,
} from '@/lib/collaborators-api';
import { Users, UserPlus, ChevronDown, ChevronUp, Trash2, Copy, Check, Link } from 'lucide-react';
import Button from '@/components/ui/Button';

export default function CollaboratorsPanel({ resourceType, resourceId }) {
  const { t } = useTranslation();
  const members = useStore(selectMembers);
  const collaborators = useStore(selectCollaborators);
  const loadedCollabFor = useStore((s) => s.loadedCollabFor);
  const setCollaborators = useStore((s) => s.setCollaborators);
  const storeAddCollaborator = useStore((s) => s.addCollaborator);
  const storeRemoveCollaborator = useStore((s) => s.removeCollaborator);
  const storeUpdateRole = useStore((s) => s.updateCollaboratorRole);

  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [selectedRole, setSelectedRole] = useState('viewer');
  const [inviteCode, setInviteCode] = useState(null);
  const [inviteRole, setInviteRole] = useState('viewer');
  const [copied, setCopied] = useState(false);
  const [generatingCode, setGeneratingCode] = useState(false);

  const resourceKey = `${resourceType}:${resourceId}`;

  useEffect(() => {
    if (expanded && loadedCollabFor !== resourceKey && isSupabaseConfigured()) {
      setLoading(true);
      fetchCollaborators(resourceType, resourceId)
        .then((list) => setCollaborators(resourceKey, list))
        .catch(() => setCollaborators(resourceKey, []))
        .finally(() => setLoading(false));
    }
  }, [expanded, resourceKey]);

  const internalCollabs = collaborators.filter((c) => c.collabType === 'internal');
  const externalCollabs = collaborators.filter((c) => c.collabType === 'external');

  const getMember = (memberId) => members.find((m) => m.id === memberId);

  const usedMemberIds = new Set(internalCollabs.map((c) => c.memberId));
  const availableMembers = members.filter((m) => !usedMemberIds.has(m.id));

  const handleAddMember = async () => {
    if (!selectedMemberId) return;
    setLoading(true);
    try {
      const result = await addInternalCollaborator(resourceType, resourceId, selectedMemberId, selectedRole);
      if (result) storeAddCollaborator(result);
      setAddingMember(false);
      setSelectedMemberId('');
      setSelectedRole('viewer');
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (id) => {
    try {
      await apiRemoveCollaborator(id);
      storeRemoveCollaborator(id);
    } catch {
      // silent
    }
  };

  const handleRoleChange = async (id, newRole) => {
    try {
      await apiUpdateCollaboratorRole(id, newRole);
      storeUpdateRole(id, newRole);
    } catch {
      // silent
    }
  };

  const handleGenerateCode = async () => {
    setGeneratingCode(true);
    try {
      const result = await generateInviteCode(resourceType, resourceId, inviteRole);
      if (result) setInviteCode(result.code);
    } catch {
      // silent
    } finally {
      setGeneratingCode(false);
    }
  };

  const handleCopy = async () => {
    if (!inviteCode) return;
    await navigator.clipboard.writeText(inviteCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="card">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 text-left"
      >
        <Users size={16} className="text-gray-400" />
        <span className="flex-1 text-xs font-medium text-gray-500 dark:text-gray-400">
          {t('collaborators.title')}
        </span>
        {collaborators.length > 0 && (
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-brand-100 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400">
            {collaborators.length}
          </span>
        )}
        {expanded ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
      </button>

      {expanded && (
        <div className="mt-3 space-y-4">
          {loading && (
            <div className="text-xs text-gray-400 text-center py-2">{t('common.loading')}</div>
          )}

          {!loading && collaborators.length === 0 && !addingMember && !inviteCode && (
            <div className="text-xs text-gray-400 dark:text-gray-500 text-center py-2">
              {t('collaborators.noCollaborators')}
            </div>
          )}

          {/* Internal members */}
          {internalCollabs.length > 0 && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1.5">
                {t('collaborators.members')}
              </div>
              <div className="space-y-1.5">
                {internalCollabs.map((c) => {
                  const member = getMember(c.memberId);
                  return (
                    <div key={c.id} className="flex items-center gap-2 py-1.5">
                      <span className="text-base">{member?.emoji || '👤'}</span>
                      <span className="flex-1 text-sm text-gray-900 dark:text-white truncate">
                        {member?.name || c.memberId}
                      </span>
                      <select
                        value={c.role}
                        onChange={(e) => handleRoleChange(c.id, e.target.value)}
                        className="text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-0"
                      >
                        <option value="viewer">{t('collaborators.role.viewer')}</option>
                        <option value="editor">{t('collaborators.role.editor')}</option>
                      </select>
                      <button
                        onClick={() => handleRemove(c.id)}
                        className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-danger-500 transition-colors"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* External collaborators */}
          {externalCollabs.length > 0 && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1.5">
                {t('collaborators.external')}
              </div>
              <div className="space-y-1.5">
                {externalCollabs.map((c) => (
                  <div key={c.id} className="flex items-center gap-2 py-1.5">
                    <Link size={14} className="text-gray-400" />
                    <span className="flex-1 text-sm text-gray-900 dark:text-white truncate">
                      {c.collaboratorId?.slice(0, 8)}...
                    </span>
                    <select
                      value={c.role}
                      onChange={(e) => handleRoleChange(c.id, e.target.value)}
                      className="text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-0"
                    >
                      <option value="viewer">{t('collaborators.role.viewer')}</option>
                      <option value="editor">{t('collaborators.role.editor')}</option>
                    </select>
                    <button
                      onClick={() => handleRemove(c.id)}
                      className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-danger-500 transition-colors"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add internal member form */}
          {addingMember ? (
            <div className="space-y-2 p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50">
              <select
                value={selectedMemberId}
                onChange={(e) => setSelectedMemberId(e.target.value)}
                className="input text-sm w-full"
              >
                <option value="">{t('collaborators.selectMember')}</option>
                {availableMembers.map((m) => (
                  <option key={m.id} value={m.id}>{m.emoji} {m.name}</option>
                ))}
              </select>
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="input text-sm w-full"
              >
                <option value="viewer">{t('collaborators.role.viewer')}</option>
                <option value="editor">{t('collaborators.role.editor')}</option>
              </select>
              <div className="flex gap-2">
                <Button size="sm" className="flex-1" onClick={handleAddMember} loading={loading}>
                  {t('common.add')}
                </Button>
                <Button size="sm" variant="ghost" className="flex-1" onClick={() => setAddingMember(false)}>
                  {t('common.cancel')}
                </Button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setAddingMember(true)}
              className="flex items-center gap-1.5 text-xs font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 transition-colors"
            >
              <UserPlus size={14} />
              {t('collaborators.addMember')}
            </button>
          )}

          {/* Invite external section */}
          <div className="pt-2 border-t border-gray-100 dark:border-gray-800">
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
              {t('collaborators.inviteExternal')}
            </div>
            {inviteCode ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-gray-100 dark:bg-gray-800 rounded-xl py-3 text-center font-mono text-lg font-bold tracking-[0.3em] text-gray-900 dark:text-white">
                    {inviteCode}
                  </div>
                  <button
                    onClick={handleCopy}
                    className="p-2.5 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  >
                    {copied ? <Check size={16} className="text-success-500" /> : <Copy size={16} className="text-gray-500" />}
                  </button>
                </div>
                <div className="text-[10px] text-gray-400 text-center">
                  {t('collaborators.codeExpiry')}
                </div>
              </div>
            ) : (
              <div className="flex gap-2">
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="input text-sm flex-shrink-0"
                >
                  <option value="viewer">{t('collaborators.role.viewer')}</option>
                  <option value="editor">{t('collaborators.role.editor')}</option>
                </select>
                <Button size="sm" className="flex-1" onClick={handleGenerateCode} loading={generatingCode}>
                  {t('collaborators.generateCode')}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
